import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";

export interface CredentialTypeSelectionModalProps {
    show: boolean;
    setShowModal: (show: boolean) => void;
    onSelectType: (type: "text" | "embedding") => void;
    title: string;
}

const CREDENTIAL_OPTIONS = [
    {
        type: "text" as const,
        title: "Text LLM Credentials",
        description: "For text generation and chat models",
    },
    {
        type: "embedding" as const,
        title: "Embedding Credentials",
        description: "For embedding and vector models",
    },
];

export default function CredentialTypeSelectionModal({
    show,
    setShowModal,
    onSelectType,
    title,
}: CredentialTypeSelectionModalProps) {
    const [selectedType, setSelectedType] = useState<"text" | "embedding" | null>(null);

    const handleContinue = () => {
        if (selectedType) {
            onSelectType(selectedType);
            setSelectedType(null);
        }
    };

    const handleClose = () => {
        setShowModal(false);
        setSelectedType(null);
    };

    return (
        <Dialog open={show} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <DialogContent className="gs-credential-type-dialog sm:max-w-2xl">
                <div className="gs-credential-type-dialog__header">
                    <h4 className="gs-credential-type-dialog__title">{title}</h4>
                </div>

                <div className="gs-credential-type-dialog__body">
                    <label className="gs-credential-type-dialog__label">Select Credential Type</label>
                    <div className="gs-credential-type-dialog__options">
                        {CREDENTIAL_OPTIONS.map((option) => {
                            const isSelected = selectedType === option.type;
                            return (
                                <div
                                    key={option.type}
                                    role="button"
                                    tabIndex={0}
                                    className={`gs-credential-type-dialog__option${isSelected ? " is-selected" : ""}`}
                                    onClick={() => setSelectedType(option.type)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            setSelectedType(option.type);
                                        }
                                    }}
                                >
                                    <div className="gs-credential-type-dialog__option-row">
                                        <input
                                            type="radio"
                                            name="credentialType"
                                            checked={isSelected}
                                            onChange={() => setSelectedType(option.type)}
                                            className="gs-credential-type-dialog__option-radio"
                                        />
                                        <div
                                            className="gs-credential-type-dialog__option-text"
                                            onClick={() => setSelectedType(option.type)}
                                        >
                                            <div className="gs-credential-type-dialog__option-title">{option.title}</div>
                                            <div className="gs-credential-type-dialog__option-desc">{option.description}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="gs-credential-type-dialog__footer">
                    <Button variant="default" disabled={!selectedType} onClick={handleContinue}>
                        {BUTTONS.CONTINUE}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
